import { Injectable } from '@angular/core';
import { toCanvas } from 'html-to-image';
import { jsPDF } from 'jspdf';

const ARABIC_FONT = '"Noto Naskh Arabic", "Segoe UI", "Tahoma", "Geeza Pro", Arial, sans-serif';
const LANDSCAPE_PAGE_WIDTH_PX = 1123;
const LANDSCAPE_PAGE_HEIGHT_PX = 794;
const INK = '#20242b';
const MUTED = '#5b6675';
const LINE = '#d0d5dd';
const HEAD_BG = '#f2f4f7';
const ROWS_PER_PAGE = 18;

export interface ParentsListPdfLabels {
  title: string;
  scopeLabel: string;
  total: string;
  empty: string;
  index: string;
  name: string;
  phones: string;
  students: string;
  classes: string;
  relation: string;
  bus: string;
  noBus: string;
}

export interface ParentsListPdfRow {
  name: string;
  phones: string;
  students: string;
  classes: string;
  relation: string;
  bus: string;
}

export interface ParentsListPdfExportInput {
  schoolName: string;
  isArabic: boolean;
  scopeText: string;
  labels: ParentsListPdfLabels;
  rows: ParentsListPdfRow[];
}

const SHEET_CSS = `
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    color: ${INK};
    font-family: ${ARABIC_FONT};
  }
  #sheet {
    width: ${LANDSCAPE_PAGE_WIDTH_PX}px;
    min-height: ${LANDSCAPE_PAGE_HEIGHT_PX}px;
    padding: 16px 20px;
    background: #ffffff;
    color: ${INK};
    font-family: ${ARABIC_FONT};
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }
  .sheet-head {
    display: flex;
    flex-direction: row;
    direction: ltr;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 12px;
    border-bottom: 2px solid ${LINE};
    padding-bottom: 10px;
  }
  .sheet-logos {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
  .sheet-logos img {
    height: 42px;
    width: auto;
    display: block;
  }
  .sheet-head-text {
    flex: 1;
    min-width: 0;
  }
  .sheet-school {
    font-size: 15px;
    font-weight: 700;
  }
  .sheet-title {
    font-size: 14px;
    font-weight: 700;
    margin-top: 4px;
  }
  .sheet-meta {
    margin-top: 4px;
    font-size: 12px;
    color: ${MUTED};
  }
  .sheet-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .summary {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 0 0 10px;
  }
  .summary-item {
    padding: 6px 10px;
    border: 1px solid ${LINE};
    border-radius: 8px;
    background: ${HEAD_BG};
    min-width: 110px;
  }
  .summary-item .label {
    display: block;
    font-size: 11px;
    color: ${MUTED};
    margin-bottom: 2px;
  }
  .summary-item .value {
    font-size: 16px;
    font-weight: 700;
  }
  table.report {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
  }
  table.report th,
  table.report td {
    border: 1px solid ${LINE};
    padding: 5px 6px;
    vertical-align: top;
    text-align: start;
  }
  table.report th {
    background: ${HEAD_BG};
    font-weight: 700;
    white-space: nowrap;
  }
  table.report th.num,
  table.report td.num {
    width: 32px;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }
  table.report td.name {
    font-weight: 650;
    min-width: 110px;
  }
  table.report td.stack {
    white-space: pre-line;
    line-height: 1.35;
  }
  .empty {
    padding: 18px;
    border: 1px dashed ${LINE};
    border-radius: 8px;
    color: ${MUTED};
    font-size: 13px;
    text-align: center;
  }
`;

@Injectable({ providedIn: 'root' })
export class ParentsListPdfService {
  private logoDataUrl = '';
  private bannerDataUrl = '';

  async export(input: ParentsListPdfExportInput): Promise<void> {
    await this.ensureLogos();

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'a4',
    });
    const margin = 18;
    let pageStarted = false;
    let globalPageIndex = 0;

    const pages = this.buildPageHtmls(input);
    const totalPages = Math.max(1, pages.length);

    for (const html of pages) {
      const canvas = await this.rasterizeHtml(html, input.isArabic);
      globalPageIndex += 1;
      if (pageStarted) {
        pdf.addPage();
      }
      this.drawCanvasPage(canvas, pdf, margin, globalPageIndex, totalPages);
      pageStarted = true;
    }

    this.savePdf(pdf, `parents-${Date.now()}.pdf`);
  }

  private buildPageHtmls(input: ParentsListPdfExportInput): string[] {
    const chunks: ParentsListPdfRow[][] = [];
    if (input.rows.length) {
      for (let i = 0; i < input.rows.length; i += ROWS_PER_PAGE) {
        chunks.push(input.rows.slice(i, i + ROWS_PER_PAGE));
      }
    } else {
      chunks.push([]);
    }

    let offset = 0;
    return chunks.map((chunk, index) => {
      const isFirst = index === 0;
      const body = [
        isFirst ? this.buildSummaryHtml(input.labels, input.rows.length) : '',
        this.buildTableHtml(input.labels, chunk, offset, input.rows.length),
      ].join('');
      offset += chunk.length;
      return this.wrapSheet(input, body);
    });
  }

  private wrapSheet(input: ParentsListPdfExportInput, bodyHtml: string): string {
    const { labels, isArabic } = input;
    const dir = isArabic ? 'rtl' : 'ltr';
    return `
      <div id="sheet" dir="${dir}">
        <div class="sheet-head">
          <div class="sheet-logos">
            <img src="${this.logoDataUrl}" alt="">
            <img src="${this.bannerDataUrl}" alt="">
          </div>
          <div class="sheet-head-text" dir="${dir}">
            <div class="sheet-school">${this.escapeHtml(input.schoolName || '')}</div>
            <div class="sheet-title">${this.escapeHtml(labels.title)}</div>
            <div class="sheet-meta">
              ${this.escapeHtml(labels.scopeLabel)}: ${this.escapeHtml(input.scopeText)}
            </div>
          </div>
        </div>
        <div class="sheet-body">${bodyHtml}</div>
      </div>`;
  }

  private buildSummaryHtml(labels: ParentsListPdfLabels, total: number): string {
    return `
      <div class="summary">
        <div class="summary-item">
          <span class="label">${this.escapeHtml(labels.total)}</span>
          <span class="value">${total}</span>
        </div>
      </div>`;
  }

  private buildTableHtml(
    labels: ParentsListPdfLabels,
    rows: ParentsListPdfRow[],
    startIndex: number,
    totalRows: number,
  ): string {
    if (!totalRows) {
      return `<div class="empty">${this.escapeHtml(labels.empty)}</div>`;
    }
    if (!rows.length) {
      return '';
    }

    const head = `<tr>
      <th class="num">${this.escapeHtml(labels.index)}</th>
      <th>${this.escapeHtml(labels.name)}</th>
      <th>${this.escapeHtml(labels.phones)}</th>
      <th>${this.escapeHtml(labels.students)}</th>
      <th>${this.escapeHtml(labels.classes)}</th>
      <th>${this.escapeHtml(labels.relation)}</th>
      <th>${this.escapeHtml(labels.bus)}</th>
    </tr>`;

    const body = rows.map((row, index) => `<tr>
      <td class="num">${startIndex + index + 1}</td>
      <td class="name">${this.escapeHtml(row.name || '—')}</td>
      <td class="stack">${this.escapeHtml(row.phones || '—')}</td>
      <td class="stack">${this.escapeHtml(row.students || '—')}</td>
      <td class="stack">${this.escapeHtml(row.classes || '—')}</td>
      <td class="stack">${this.escapeHtml(row.relation || '—')}</td>
      <td class="stack">${this.escapeHtml(row.bus || '—')}</td>
    </tr>`).join('');

    return `
      <table class="report">
        <thead>${head}</thead>
        <tbody>${body}</tbody>
      </table>`;
  }

  private drawCanvasPage(
    canvas: HTMLCanvasElement,
    pdf: jsPDF,
    margin: number,
    pageIndex: number,
    totalPages: number,
  ): void {
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const footerReserve = 22;
    const maxImgWidth = pageW - margin * 2;
    const maxImgHeight = pageH - margin * 2 - footerReserve;
    const scale = Math.min(maxImgWidth / canvas.width, maxImgHeight / canvas.height);
    const drawWidth = canvas.width * scale;
    const drawHeight = canvas.height * scale;
    const x = margin + (maxImgWidth - drawWidth) / 2;

    pdf.addImage(
      canvas.toDataURL('image/png'),
      'PNG',
      x,
      margin,
      drawWidth,
      drawHeight,
      undefined,
      'NONE',
    );
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(91, 102, 117);
    pdf.text(`${pageIndex}/${totalPages}`, pageW / 2, pageH - Math.max(10, margin * 0.55), {
      align: 'center',
    });
  }

  private resolveAssetUrl(relativePath: string): string {
    return new URL(relativePath, document.baseURI).href;
  }

  private async toDataUrl(src: string): Promise<string> {
    const response = await fetch(src);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error(`Failed to read image: ${src}`));
      reader.readAsDataURL(blob);
    });
  }

  private async ensureLogos(): Promise<void> {
    if (this.logoDataUrl && this.bannerDataUrl) {
      return;
    }
    const [logo, banner] = await Promise.all([
      this.toDataUrl(this.resolveAssetUrl('assets/weekly-plan/school-logo.png')),
      this.toDataUrl(this.resolveAssetUrl('assets/weekly-plan/year-banner.jpg')),
    ]);
    this.logoDataUrl = logo;
    this.bannerDataUrl = banner;
  }

  private escapeHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private wrapDocument(innerHtml: string, isArabic: boolean): string {
    return `<!DOCTYPE html>
<html lang="${isArabic ? 'ar' : 'en'}" dir="${isArabic ? 'rtl' : 'ltr'}">
<head>
  <meta charset="utf-8">
  <link rel="stylesheet" href="/assets/fonts/fonts.css">
  <style>${SHEET_CSS}</style>
</head>
<body>${innerHtml}</body>
</html>`;
  }

  private async waitFrames(targetWindow: Window = window): Promise<void> {
    const fonts = targetWindow.document?.fonts;
    if (fonts) {
      try {
        await fonts.load('400 13px "Noto Naskh Arabic"');
        await fonts.load('700 13px "Noto Naskh Arabic"');
        await fonts.ready;
      } catch {
        // Capture still runs if the face is unavailable.
      }
    }
    await new Promise<void>(resolve => {
      targetWindow.requestAnimationFrame(() => {
        targetWindow.requestAnimationFrame(() => resolve());
      });
    });
  }

  private async rasterizeHtml(innerHtml: string, isArabic: boolean): Promise<HTMLCanvasElement> {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.left = '0';
    iframe.style.top = '0';
    iframe.style.width = `${LANDSCAPE_PAGE_WIDTH_PX}px`;
    iframe.style.height = '240px';
    iframe.style.opacity = '0.01';
    iframe.style.pointerEvents = 'none';
    iframe.style.border = '0';
    iframe.style.zIndex = '-1';
    iframe.style.background = '#ffffff';

    const loaded = new Promise<void>(resolve => {
      iframe.onload = () => resolve();
    });
    iframe.srcdoc = this.wrapDocument(innerHtml, isArabic);
    document.body.appendChild(iframe);

    try {
      await loaded;
      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      if (!doc || !win) {
        throw new Error('Failed to prepare PDF page');
      }
      await this.waitFrames(win);
      const sheet = doc.getElementById('sheet');
      if (!sheet) {
        throw new Error('PDF sheet missing');
      }
      const height = Math.max(
        LANDSCAPE_PAGE_HEIGHT_PX,
        Math.ceil(sheet.scrollHeight || LANDSCAPE_PAGE_HEIGHT_PX),
      );
      iframe.style.height = `${height}px`;
      await this.waitFrames(win);

      return await toCanvas(sheet as HTMLElement, {
        pixelRatio: 2,
        width: LANDSCAPE_PAGE_WIDTH_PX,
        height,
        backgroundColor: '#ffffff',
        cacheBust: true,
      });
    } finally {
      iframe.remove();
    }
  }

  private savePdf(pdf: jsPDF, filename: string): void {
    const blob = pdf.output('blob');
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  }
}
